import { ResultOf } from "gql.tada";
import { cloneElement, ComponentProps, forwardRef, useMemo } from "react";
// eslint-disable-next-line no-restricted-imports
import { Link as RRLink } from "react-router";
import { LinkFragment } from "../../../queries/fragments/LinkFragment";
import { useArticleModalState } from "../../../hooks/useArticleModalState";
import { useGlobalContext } from "../../../utils/GlobalContext";

type LinkProps = { noLocalePrefix?: boolean } & (
  | (ComponentProps<"a"> & { href: string; link?: never })
  | (ComponentProps<"a"> & {
      link: ResultOf<typeof LinkFragment>;
      href?: never;
    })
);

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ href: hrefProp, link, noLocalePrefix = false, ...props }, ref) => {
    const { locale } = useGlobalContext();
    const { setIsArticleModalOpen } = useArticleModalState();
    const isArticlePage = link?.pageReference?.__typename === "ArticlePage";
    const href = useMemo(() => {
      const localePrefix = noLocalePrefix
        ? ""
        : `/${locale.toLocaleLowerCase()}`;
      if (link) {
        return link.isExternal
          ? link.url
          : localePrefix +
              (link.pageReference?.slug
                ? `${isArticlePage ? "/article" : ""}/${
                    link.pageReference.slug
                  }`
                : "#");
      }
      const internalHref = hrefProp.startsWith("/");
      return (
        (internalHref ? localePrefix : "") +
        (hrefProp === "/homepage" ? "/" : hrefProp ?? "#")
      );
    }, [hrefProp, link, isArticlePage, noLocalePrefix, locale]) as string;

    const isInternal = useMemo(() => {
      const url = new URL(
        href,
        `${window.location.protocol}//${window.location.host}`
      );
      return url.host === window.location.host;
    }, [href]);

    if (isInternal) {
      const linkElement = <RRLink {...props} to={href} ref={ref} />;

      if (isArticlePage) {
        return cloneElement(linkElement, {
          onClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            props.onClick?.(e);
            if (e.isDefaultPrevented()) return;
            setIsArticleModalOpen(link.pageReference?.slug ?? "");
            e.preventDefault();
            e.currentTarget.blur();
          },
        });
      } else return linkElement;
    } else {
      return (
        // eslint-disable-next-line react/forbid-elements
        <a
          {...props}
          href={href}
          ref={ref}
          target="_blank"
          rel="noopener noreferrer"
        />
      );
    }
  }
);
